// @ts-check
import { useEffect } from "react";
import { useEngine, useEngineMetrics, useFrameStats, useLensErrors } from "./EngineProvider.js";
import { useLensHost } from "./useLensHost.js";
import { dispatchEngineCommand } from "./engineCommandDispatcher.js";
import { LensLoadError, serializeLensLoadError } from "./lensLoadError.js";
function serializeCommandError(error) {
    if (error instanceof LensLoadError)
        return serializeLensLoadError(error);
    const normalized = error instanceof Error ? error : new Error(String(error || "Unknown command error"));
    return {
        name: normalized.name || "Error",
        message: normalized.message,
        stack: normalized.stack || null,
    };
}
/**
 * Bridges `window.lensHost` (if present) to the engine running in the current
 * `<EngineProvider>` subtree.
 *
 * Wires three flows:
 *
 *  1. Host → engine: `host.onCommand(...)` events are dispatched through
 *     {@link dispatchEngineCommand} into `useEngine().actions`. The result is
 *     reported back via `host.commandResult(...)`.
 *  2. Engine → host: every change to engine readiness, source, or frame count
 *     is pushed via `host.setState({...})`.
 *  3. Boot handshake: `host.ready()` is called once after the engine is ready.
 *
 * When no host is present (plain browser tab) this hook is a no-op.
 *
 * Must be called from inside an `<EngineProvider>`.
 */
export function useHostBridge() {
    const host = useLensHost();
    const { engine, actions, ready, source, error } = useEngine();
    const frameCount = useFrameStats((s) => s.frameCount);
    const measuredFps = useFrameStats((s) => s.measuredFps);
    const engineMetrics = useEngineMetrics();
    const lensErrors = useLensErrors();
    const activeLenses = useFrameStats((s) => s.activeLenses);
    const coreVersion = useFrameStats((s) => s.coreVersion);
    // Subscribe to commands from the host.
    useEffect(() => {
        if (!host?.onCommand)
            return undefined;
        return host.onCommand(async ({ requestId, command, payload }) => {
            try {
                const result = await dispatchEngineCommand(command, payload, actions, engine);
                host.commandResult?.({ requestId, ok: true, result });
            }
            catch (err) {
                host.commandResult?.({
                    requestId,
                    ok: false,
                    error: serializeCommandError(err),
                });
            }
        });
    }, [host, actions, engine]);
    // One-shot boot handshake.
    useEffect(() => {
        if (!host?.ready)
            return;
        if (!ready)
            return;
        globalThis.__lenscoreHostReady = true;
        host.ready();
        return () => {
            globalThis.__lenscoreHostReady = false;
        };
    }, [host, ready]);
    // Mirror engine state up to the host.
    useEffect(() => {
        if (!host?.setState)
            return;
        host.setState({
            ready,
            source,
            frameCount,
            measuredFps,
            engineMetrics,
            lensErrors,
            activeLenses,
            coreVersion,
            deviceClass: engine?.deviceClass ?? null,
            error: error?.message ?? null,
            lensError: error instanceof LensLoadError ? serializeLensLoadError(error) : null,
        });
    }, [host, ready, source, frameCount, measuredFps, engineMetrics, lensErrors, activeLenses, coreVersion, engine?.deviceClass, error]);
}
//# sourceMappingURL=useHostBridge.js.map